import { useCallback, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { Popper, styled } from '@mui/material'
import { Option } from 'components/form-elements/FormSelect'
import { useShowControl } from 'hooks/useShowControl'
import { FilterModel } from 'types'

const StyledTextField = styled(TextField)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-root': {
    paddingLeft: `calc(1em + ${theme.spacing(3)}) !important`,
    paddingRight: `6px !important`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '25ch',
    },
  },
}))

const CustomPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-paper': {
    backgroundColor: '#a6146040',
  },
}))

export interface SearchInputProps {
  options: Option[] | undefined
  isLoading: boolean
  getFilters: (value: number) => FilterModel
  onSearch: (searchText: string, filter: FilterModel | null) => void
}

const isOptionEqualToValue = (option: Option, value: Option) => option.value === value.value
const getOptionLabel = (option: Option) => option.label
const getLowerLabel = (option: Option) => option.label.toString().toLowerCase()

export function useSearchInput({ options, isLoading, getFilters, onSearch }: SearchInputProps) {
  const [search, setSearch] = useState<Option | null>(null)
  const [searchInput, setSearchInput] = useState('')

  const [open, onOpen, onClose] = useShowControl(false)

  const filteredOptions = useMemo(() => {
    if (!searchInput || !options) return []
    const lowerInput = searchInput.toLowerCase()

    const match = options
      .filter((opt) => getLowerLabel(opt).includes(lowerInput))
      .sort(
        (a, b) => getLowerLabel(a).indexOf(lowerInput) - getLowerLabel(b).indexOf(lowerInput),
      )[0]

    if (match) {
      onOpen()
      return [match]
    }

    return []
  }, [onOpen, searchInput, options])

  const onChangeSearchInput = (e: React.SyntheticEvent<Element, Event>, value: string) => {
    if (!value && (!e || e.type === 'blur')) return
    setSearchInput(value)
  }

  const onChangeSearch = useCallback(
    (e: React.SyntheticEvent, value: Option | null) => {
      setSearch(value)
      if (value) {
        onSearch('', getFilters(+value.value))
      }
    },
    [getFilters, onSearch],
  )

  const SearchInput = useMemo(() => {
    return (
      <Autocomplete
        PopperComponent={CustomPopper}
        popupIcon={null}
        size="small"
        open={open}
        onOpen={() => {}}
        onClose={onClose}
        value={search}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={(a) => getOptionLabel(a).toString()}
        onChange={onChangeSearch}
        inputValue={searchInput}
        onInputChange={onChangeSearchInput}
        loading={isLoading}
        options={filteredOptions}
        renderInput={(params) => <StyledTextField {...params} placeholder="Enter search terms…" />}
      />
    )
  }, [isLoading, onChangeSearch, onClose, open, filteredOptions, search, searchInput])

  return { SearchInput, search, searchInput }
}
